import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { formatBetNumber } from "../common/utils/formatBetNumber";
import { getBetTypeLabel } from "../common/utils/betTypeLabel";

// Shared by both row layouts below. Tailwind needs complete, static class
// names to pick them up, so the alignment variants are spelled out rather
// than built with string interpolation.
function RatePayoutInfo({ data, align = "start" }) {
  return (
    <div
      className={
        (align === "end"
          ? "flex flex-col items-end gap-0.5"
          : "flex flex-col items-start gap-0.5") +
        " text-brand-gold-100 whitespace-nowrap"
      }
    >
      <div className="flex flex-row items-center gap-1">
        <span className="font-thin text-[11px] text-white/50">บาทละ:</span>
        <span className="font-thin text-[13px]">
          {Math.round(Number(data.bet_payrate))}
        </span>
      </div>
      <div className="flex flex-row items-center gap-1">
        <span className="font-thin text-[11px] text-white/50">ราคาจ่าย</span>
        <span className="font-semibold text-[13px] text-brand-gold-300">
          {(data.bet_amout * data.bet_payrate).toLocaleString("en-US")}
        </span>
      </div>
    </div>
  );
}

// Name lookup comes from `codeMeta` (numadd.jsx, derived from the current
// lotto type's own rewardTypeGroups — the same source "รายการที่เลือก"
// uses) instead of a separate, unfiltered /getLottoRewardTypes fetch,
// which didn't reliably have every code (e.g. run_top_mid, run_bot_end)
// for the lotto type actually being bet on.
function Lottotype(props) {
  const { t } = useTranslation();
  const handleDelete = props.handleDelete;
  const handleChange = props.handleChange;
  // At 530px and below there isn't room for the single-row editable layout
  // (the delete icon gets clipped) — those screens get a compact, read-only
  // two-line row instead. Above 530px keeps the original editable row.
  const isCompact = useMediaQuery("(max-width: 530px)");

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col space-y-2 border-dashed border-b-[1px] border-slate-400"
    >
      {Object.keys(props.grouparray).map((code) => (
        <div key={code} className="space-y-1">
          <div className="bg-gradient-to-r from-brand-blue-600 to-brand-blue-800 rounded-full place-content-center flex justify-center px-2 py-0.5 shadow-sm">
            <span className="text-brand-gold-200 font-bold text-xs tracking-wide">
              {getBetTypeLabel(code, props.codeMeta?.[code]?.name, t)}
            </span>
          </div>
          {props.grouparray[code].map((data) =>
            isCompact ? (
              <div key={data.id}>
                <div className="numlist bg-brand-blue-800/60 border border-brand-blue-600 rounded-lg p-1.5 flex flex-col gap-1 shadow-sm hover:border-brand-gold-300 transition-colors">
                  <div className="flex flex-row items-center justify-between">
                    <span className="text-brand-gold-200 font-bold text-[15px] whitespace-nowrap">
                      {formatBetNumber(data.number)}
                    </span>
                    <DeleteForeverIcon
                      onClick={() => handleDelete(data.id)}
                      className="text-rose-500 hover:text-rose-400 cursor-pointer transition-colors"
                      fontSize="small"
                    />
                  </div>

                  <div className="flex flex-col gap-0 whitespace-nowrap">
                    <span className="font-bold text-[13px] text-brand-gold-100">
                      {data.bet_amout} ฿
                    </span>
                    <span className="font-semibold text-[11px] text-brand-gold-100/60">
                      x {Math.round(Number(data.bet_payrate))} ={" "}
                      {(data.bet_amout * data.bet_payrate).toLocaleString(
                        "en-US",
                      )}{" "}
                      ฿
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div key={data.id}>
                <div className="numlist bg-brand-blue-800/60 border border-brand-blue-600 rounded-lg p-1.5 flex flex-row items-center gap-3 justify-around shadow-sm hover:border-brand-gold-300 transition-colors">
                  <span className="text-brand-gold-200 font-bold text-[15px] whitespace-nowrap">
                    {formatBetNumber(data.number)}
                  </span>

                  {/* set bet amont */}
                  <input
                    className="text-brand-blue-900 text-center w-[60px] rounded p-1"
                    type="number"
                    value={data.bet_amout}
                    onChange={(e) => handleChange(e, data.id)}
                    min={1}
                    max={5000}
                  />
                  <RatePayoutInfo data={data} />

                  <div>
                    <DeleteForeverIcon
                      onClick={() => handleDelete(data.id)}
                      className="text-rose-500 hover:text-rose-400 cursor-pointer transition-colors"
                    />
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      ))}
    </motion.div>
  );
}

export default Lottotype;
